/**
 * Renders an assistant chat message as proper Markdown (bold, lists, tables,
 * code, links) — the backend replies in Markdown.
 *
 * CLONED from the Console (tc-lab src/MessageContent.tsx), including the
 * marker protocol: `[View Title|surface:id?qs]` and `[Show me|surface:id …]`
 * are first rewritten into normal Markdown links with private `tcsurface:` /
 * `tcshow:` schemes, then mapped to MUI buttons. LV delta: the surface icon
 * comes from the LV registry (icon string → SurfaceGlyph) instead of the
 * console's SurfaceIcon map, and the marker types live in ./types.
 */
import { memo, useMemo } from 'react'
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Box, Button, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'
import { SurfaceGlyph } from '../shell/icons'
import { surfaceById } from '../shell/registry'
import type { ShowInSurfaceArgs, SurfaceOpenTarget } from './types'

const SURFACE_MARKER = /\[View ([^\]|]+)\|surface:([a-z0-9-]+)(\?[^\]]*)?\]/g
const SURFACE_SCHEME = 'tcsurface:'
// [Show me|surface:<id><?query> anchor:<id> | text:"<label>" | selector:<css>]
// — the assistant pointing at something inside a surface. The highlight spec
// rides in the link's hash so the query string stays purely navigational.
const SHOW_MARKER = /\[Show me\|surface:([a-z0-9-]+)(\?[^\]\s]*)?\s+(anchor:([^\]\s]+)|text:"([^"]+)"|selector:([^\]]+))\]/g
const SHOW_SCHEME = 'tcshow:'

// Hoisted module constants (the console's streaming fix, ported): inline
// literals here used to defeat react-markdown's caching on EVERY render —
// during streaming that's the whole transcript re-parsing per token.
const REMARK_PLUGINS = [remarkGfm]
const urlTransform = (url: string) =>
  url.startsWith(SURFACE_SCHEME) || url.startsWith(SHOW_SCHEME) ? url : defaultUrlTransform(url)

/** The marker button's leading icon, by surface id. */
function MarkerIcon({ id }: { id: string }) {
  return <SurfaceGlyph icon={surfaceById(id)?.icon ?? 'Tab'} size={14} />
}

/** Turn the assistant's surface markers into normal Markdown links with
 *  private schemes (tcsurface: / tcshow:) that components.a intercepts. */
function markersToLinks(content: string): string {
  return content
    .replace(SHOW_MARKER, (_all, id: string, qs: string | undefined, _target: string, anchor?: string, text?: string, selector?: string) => {
      const spec = anchor !== undefined
        ? `anchor=${encodeURIComponent(anchor)}`
        : text !== undefined
          ? `text=${encodeURIComponent(text)}`
          : `selector=${encodeURIComponent(selector ?? '')}`
      return `[Show me](${SHOW_SCHEME}${id}${qs ?? ''}#${spec})`
    })
    .replace(SURFACE_MARKER, (_all, title: string, id: string, qs?: string) =>
      `[${title}](${SURFACE_SCHEME}${id}${qs ?? ''})`)
}

function parseTarget(qs: string): SurfaceOpenTarget | undefined {
  if (!qs) return undefined
  const usp = new URLSearchParams(qs.slice(1))
  const view = usp.get('view') ?? undefined
  usp.delete('view')
  const params: Record<string, string> = {}
  for (const [k, v] of usp.entries()) params[k] = v
  return view || Object.keys(params).length ? { view, params } : undefined
}

function MessageContent({
  content,
  onOpenSurface,
  onShowInSurface,
}: {
  content: string
  onOpenSurface: (id: string, label?: string, target?: SurfaceOpenTarget) => void
  onShowInSurface: (args: ShowInSurfaceArgs) => void
}) {
  // Referentially stable across re-renders unless the callbacks change —
  // what lets react-markdown reconcile instead of tearing down per token.
  const components: Components = useMemo(() => ({
    p: ({ children }) => (
      <Typography variant="body2" component="p" sx={{ my: 0.75, '&:first-of-type': { mt: 0 }, '&:last-child': { mb: 0 } }}>
        {children}
      </Typography>
    ),
    strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>,
    em: ({ children }) => <Box component="em" sx={{ fontStyle: 'italic' }}>{children}</Box>,
    ul: ({ children }) => (
      <Box component="ul" sx={{ my: 0.75, pl: 3, '& li': { mb: 0.25 } }}>{children}</Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ my: 0.75, pl: 3, '& li': { mb: 0.25 } }}>{children}</Box>
    ),
    li: ({ children }) => (
      <Typography component="li" variant="body2" sx={{ display: 'list-item' }}>{children}</Typography>
    ),
    h1: ({ children }) => <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 1.5, mb: 0.5 }}>{children}</Typography>,
    h2: ({ children }) => <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 1.5, mb: 0.5 }}>{children}</Typography>,
    h3: ({ children }) => <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 1.25, mb: 0.5 }}>{children}</Typography>,
    h4: ({ children }) => <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, mb: 0.25 }}>{children}</Typography>,
    h5: ({ children }) => <Typography variant="body2" fontWeight={700} sx={{ mt: 1, mb: 0.25 }}>{children}</Typography>,
    h6: ({ children }) => <Typography variant="body2" fontWeight={700} sx={{ mt: 1, mb: 0.25 }}>{children}</Typography>,
    blockquote: ({ children }) => (
      <Box
        component="blockquote"
        sx={{ my: 0.75, pl: 1.5, ml: 0, borderLeft: '3px solid', borderColor: 'divider', color: 'text.secondary' }}
      >
        {children}
      </Box>
    ),
    hr: () => <Box component="hr" sx={{ my: 1, border: 0, borderTop: '1px solid', borderColor: 'divider' }} />,
    code: ({ className, children }) => {
      const isBlock = /\blanguage-/.test(className ?? '')
      if (isBlock) {
        return (
          <Box
            component="pre"
            sx={{
              my: 0.75, p: 1.25, borderRadius: 1.5, overflowX: 'auto',
              bgcolor: 'action.hover', fontSize: 12.5, lineHeight: 1.5,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            }}
          >
            <Box component="code">{children}</Box>
          </Box>
        )
      }
      return (
        <Box
          component="code"
          sx={{
            px: 0.5, py: 0.1, borderRadius: 0.75, bgcolor: 'action.hover',
            fontSize: '0.85em', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          }}
        >
          {children}
        </Box>
      )
    },
    table: ({ children }) => (
      <TableContainer sx={{ my: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
        <Table size="small">{children}</Table>
      </TableContainer>
    ),
    thead: ({ children }) => <TableHead>{children}</TableHead>,
    tbody: ({ children }) => <TableBody>{children}</TableBody>,
    tr: ({ children }) => <TableRow>{children}</TableRow>,
    th: ({ children }) => <TableCell sx={{ fontWeight: 700, fontSize: 12.5 }}>{children}</TableCell>,
    td: ({ children }) => <TableCell sx={{ fontSize: 12.5 }}>{children}</TableCell>,
    a: ({ href, children }) => {
      if (href && href.startsWith(SHOW_SCHEME)) {
        const rest = href.slice(SHOW_SCHEME.length)
        const hashIdx = rest.indexOf('#')
        const spec = hashIdx === -1 ? '' : rest.slice(hashIdx + 1)
        const head = hashIdx === -1 ? rest : rest.slice(0, hashIdx)
        const qIdx = head.indexOf('?')
        const id = qIdx === -1 ? head : head.slice(0, qIdx)
        const qs = qIdx === -1 ? '' : head.slice(qIdx)
        const eq = spec.indexOf('=')
        const kind = eq === -1 ? '' : spec.slice(0, eq)
        const value = eq === -1 ? '' : decodeURIComponent(spec.slice(eq + 1))
        const highlight: ShowInSurfaceArgs['highlight'] =
          kind === 'anchor' ? { anchor: value } : kind === 'text' ? { text: value } : { selector: value }
        const target = parseTarget(qs)
        return (
          <Button
            size="small" variant="contained"
            startIcon={<CenterFocusStrongIcon sx={{ fontSize: 14 }} />}
            sx={{ mx: 0.5, verticalAlign: 'middle', textTransform: 'none' }}
            onClick={() => onShowInSurface({ surfaceId: id, view: target?.view, params: target?.params, highlight })}
          >
            Show me
          </Button>
        )
      }
      if (href && href.startsWith(SURFACE_SCHEME)) {
        const rest = href.slice(SURFACE_SCHEME.length)
        const qIdx = rest.indexOf('?')
        const id = qIdx === -1 ? rest : rest.slice(0, qIdx)
        const qs = qIdx === -1 ? '' : rest.slice(qIdx)
        return (
          <Button
            size="small" variant="outlined"
            startIcon={<MarkerIcon id={id} />}
            sx={{ mx: 0.5, verticalAlign: 'middle' }}
            onClick={() => onOpenSurface(id, typeof children === 'string' ? children : undefined, parseTarget(qs))}
          >
            {children}
          </Button>
        )
      }
      return <Link href={href} target="_blank" rel="noopener noreferrer">{children}</Link>
    },
  }), [onOpenSurface, onShowInSurface])

  // Marker rewriting is pure in content — once per content change, not on
  // every render of an unchanging bubble.
  const markdown = useMemo(() => markersToLinks(content), [content])

  return (
    <Box sx={{ '& > :first-of-type': { mt: 0 }, '& > :last-child': { mb: 0 } }}>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        urlTransform={urlTransform}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
    </Box>
  )
}

// Memoized (the console's streaming fix, ported): during streaming the
// transcript re-renders per chunk, and a settled bubble's props are
// unchanged — so it skips the markdown re-parse entirely. Only the
// streaming bubble pays parse-per-chunk.
export default memo(MessageContent)
